import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useAuthState, useAuthDispatch } from "context";
import { apiFetch, formatUiErrors } from "utils";
import Button from "components/Button";
import FormField from "components/FormField";
import ConfirmationModal from "components/ConfirmationModal";
import AlertModal from "components/AlertModal";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";

const CleanupSchema = Yup.object().shape({
  organization_id: Yup.number()
    .required("Organization ID is required")
    .positive()
    .integer(),
});

const NullIntakeCleanup = () => {
  const { token } = useAuthState();
  const dispatch = useAuthDispatch();
  const [preview, setPreview] = useState(null);
  const [isExecuted, setIsExecuted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const handlePreview = async (values) => {
    setIsLoadingPreview(true);
    try {
      const response = await apiFetch({
        token,
        dispatch,
        method: "POST",
        endpoint: "/api/internal/admin/null_intake_cleanup",
        body: { organization_id: values.organization_id, preview: true },
      });
      setPreview(response.data);
      setIsExecuted(false);
    } catch (error) {
      toast.error(formatUiErrors(error));
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleExecute = async () => {
    try {
      await apiFetch({
        token,
        dispatch,
        method: "POST",
        endpoint: "/api/internal/admin/null_intake_cleanup",
        body: { organization_id: preview.organizationId },
      });
      setIsExecuted(true);
      toast.success("Cleanup job queued successfully!");
    } catch (error) {
      toast.error(formatUiErrors(error));
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-2">
        ShelterLuv — Null Intake Animal Events Cleanup
      </h1>
      <p className="text-gray-600 mb-6">
        This tool removes AnimalEvent records for ShelterLuv animals with no
        intake date (<code>last_intake_unix_time: null</code>) for a specific
        organization. This action is irreversible — reaggregation will be
        required afterward.
      </p>

      <div className="bg-white rounded p-6 border shadow-sm mb-6">
        <Formik
          initialValues={{ organization_id: "" }}
          validationSchema={CleanupSchema}
          onSubmit={handlePreview}
        >
          {({ isValid, dirty }) => (
            <Form>
              <div className="mb-4">
                <FormField
                  name="organization_id"
                  label="Organization ID"
                  type="number"
                  placeholder="e.g. 261"
                />
              </div>
              <Button
                type="submit"
                disabled={!isValid || !dirty || isLoadingPreview}
                isLoading={isLoadingPreview}
                loadingText="Loading..."
              >
                Preview
              </Button>
            </Form>
          )}
        </Formik>
      </div>

      {preview && !isExecuted && (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-6 mb-6">
          <div className="flex items-start text-yellow-800 mb-4">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="mr-3 text-xl mt-1"
            />
            <div>
              <p className="font-bold text-lg mb-2">
                Preview — Organization {preview.organizationId}
              </p>
              <p className="text-sm mb-1">
                <strong>{preview.animalsAffected}</strong> animals with null
                intake date found.
              </p>
              <p className="text-sm mb-4">
                <strong>{preview.eventsToDelete}</strong> AnimalEvent records
                will be deleted.
              </p>
              <p className="text-sm font-semibold text-red-700">
                This action is irreversible. Reaggregation will be required
                after the cleanup.
              </p>
            </div>
          </div>
          <Button
            onClick={() => setIsModalOpen(true)}
            disabled={preview.eventsToDelete === 0}
          >
            Execute Cleanup for Org {preview.organizationId}
          </Button>
          {preview.eventsToDelete === 0 && (
            <p className="text-sm text-green-700 mt-2">
              No AnimalEvent records found to delete for this organization.
            </p>
          )}
        </div>
      )}

      {isExecuted && (
        <div className="bg-green-50 border border-green-200 rounded p-6">
          <div className="flex items-center text-green-700">
            <FontAwesomeIcon icon={faCheckCircle} className="mr-3 text-xl" />
            <div>
              <p className="font-bold">Cleanup job queued successfully!</p>
              <p className="text-sm">
                Deleted {preview.eventsToDelete} AnimalEvent records for org{" "}
                {preview.organizationId}. Please reaggregate the affected
                months.
              </p>
            </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <AlertModal
          isModalOpen={isModalOpen}
          closeModal={() => setIsModalOpen(false)}
        >
          <ConfirmationModal
            prompt={`Are you sure you want to delete ${preview.eventsToDelete} AnimalEvent records for org ${preview.organizationId}? This action is irreversible and reaggregation will be required afterward.`}
            closeModal={() => setIsModalOpen(false)}
            confirmModal={() => {
              setIsModalOpen(false);
              handleExecute();
            }}
          />
        </AlertModal>
      )}
    </div>
  );
};

export default NullIntakeCleanup;
